import React, { useEffect, useState } from "react";
import "./dashboard-layout.scss";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import AuthHelper from "../../../features/authentication/helpers/auth-helper/auth-helper";
import QRViewer from "../qr-viewer/qr-viewer";
import BulkRoomCreateModal from "../../../features/manage-contacts/pages/manage-contacts-page/components/bulk-room-create/bulk-room-create";
import OutsideClickHandler from "react-outside-click-handler";
import { Tooltip } from "react-tooltip";
import OrganizationApi from "../../../api/organization-api";

const DashboardLayout = ({ children }) => {
  const { userInfo } = useSelector((state) => state.userStore);
  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );
  const [orgDetails, setOrgDetails] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [canManageUsers, setCanManageUsers] = React.useState(false);
  const [canAccessDeveloperTools, setCanAccessDeveloperTools] = React.useState(false);

  const location = useLocation();

  const _logout = (e) => {
    e.preventDefault();
    AuthHelper.logout();
  };

  useEffect(() => {
    if (userInfo?.role === "admin") {
      setCanManageUsers(true);
      setCanAccessDeveloperTools(true);
    }
  }, [userInfo]);

  useEffect(() => {
    let isMounted = true;
    const fetchOrg = async () => {
      if (!organizationId) {
        setOrgDetails(null);
        return;
      }
      try {
        setOrgLoading(true);
        const response = await OrganizationApi.private.fetchOrganization(organizationId);
        const data = response?.data?.data;
        if (isMounted) setOrgDetails(data || null);
      } catch (err) {
        console.error("Error fetching organization details:", err);
        if (isMounted) setOrgDetails(null);
      } finally {
        if (isMounted) setOrgLoading(false);
      }
    };
    fetchOrg();
    return () => {
      isMounted = false;
    };
  }, [organizationId]);

  const [profileDropdownShow, setProfileDropdownShow] = useState(false);
  const organizationName = orgDetails?.name || (orgLoading ? "Loading..." : "Your Organization");
  const orgInitial = orgDetails?.name
    ? (orgDetails.name.trim()[0] || "?").toUpperCase()
    : (orgLoading ? "…" : "?");

  const [toggleSidebar, setToggleSidebar] = useState(false);

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <OutsideClickHandler onOutsideClick={() => setToggleSidebar(false)}>
        <div className={"left-sidebar " + (toggleSidebar ? "show" : "")}>
          {/* LOGO */}
          <div className="brand">
            <a href="index.html" className="logo">
              <span>
                <img
                  src="assets/images/small_icon_withBg.png"
                  alt="logo-small"
                  className="logo-sm"
                />
              </span>
            </a>
          </div>
          <Tooltip id="TooltipConnectContacts" />
          <Tooltip id="TooltipMessages" />
          <Tooltip id="TooltipTranscripts" />
          <Tooltip id="TooltipFeatures" />
          <Tooltip id="TooltipUserManagement" />
          <Tooltip id="TooltipContactList" />
          <Tooltip id="TooltipDeveloperAccess" />
          <Tooltip id="TooltipExternalTemplates" />
          <div className="menu-content h-100" data-simplebar>
            <div className="menu-body navbar-vertical">
              <div
                className="collapse navbar-collapse tab-content"
                id="sidebarCollapse"
              >
                <ul
                  className="navbar-nav tab-pane active"
                  id="Main"
                  role="tabpanel"
                >
                  <Link
                    className={`nav-link ${
                      location.pathname === "/connect-contacts" ? "active" : ""
                    }`}
                    to="/connect-contacts"
                    // title="Connect Contacts"
                    data-tooltip-id="TooltipConnectContacts"
                    data-tooltip-content="Connect Contacts"
                  >
                    <svg fill="#000000" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 465.00 465.00" stroke="#000000" stroke-width="10.695"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M117.5,173c34.462,0,62.5-28.037,62.5-62.5S151.962,48,117.5,48S55,76.037,55,110.5S83.038,173,117.5,173z M117.5,63 c26.191,0,47.5,21.309,47.5,47.5S143.691,158,117.5,158S70,136.691,70,110.5S91.309,63,117.5,63z"></path> <path d="M282.5,173c34.462,0,62.5-28.037,62.5-62.5S316.962,48,282.5,48S220,76.037,220,110.5S248.038,173,282.5,173z M282.5,63 c26.191,0,47.5,21.309,47.5,47.5S308.691,158,282.5,158S235,136.691,235,110.5S256.309,63,282.5,63z"></path> <path d="M363.656,256.882c1.478,1.912,3.697,2.915,5.94,2.914c1.601,0,3.215-0.511,4.581-1.565 c3.278-2.533,3.881-7.243,1.349-10.521C353.077,218.66,319.17,202,282.5,202c-23.545,0-46.264,6.94-65.7,20.071 c-3.433,2.319-4.335,6.981-2.017,10.413c2.32,3.434,6.982,4.336,10.413,2.017C242.144,223.052,261.959,217,282.5,217 C314.489,217,344.07,231.536,363.656,256.882z"></path> <path d="M117.5,202C52.71,202,0,254.71,0,319.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5C15,262.981,60.981,217,117.5,217 S220,262.981,220,319.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5C235,254.71,182.29,202,117.5,202z"></path> <path d="M392.5,272c-39.977,0-72.5,32.523-72.5,72.5s32.523,72.5,72.5,72.5s72.5-32.523,72.5-72.5S432.477,272,392.5,272z M392.5,402c-31.706,0-57.5-25.794-57.5-57.5s25.794-57.5,57.5-57.5s57.5,25.794,57.5,57.5S424.206,402,392.5,402z"></path> <path d="M417.5,337H400v-17.5c0-4.143-3.358-7.5-7.5-7.5s-7.5,3.357-7.5,7.5V337h-17.5c-4.142,0-7.5,3.357-7.5,7.5 s3.358,7.5,7.5,7.5H385v17.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5V352h17.5c4.142,0,7.5-3.357,7.5-7.5 S421.642,337,417.5,337z"></path> </g> </g></svg>
                    <span>Connect Contacts</span>
                  </Link>
                  <Link
                    className={`nav-link position-relative ${
                      location.pathname === "/transcript-chats" ? "active" : ""
                    }`}
                    to="/transcript-chats"
                    // title="Messages"
                    data-tooltip-id="TooltipTranscripts"
                    data-tooltip-content="Transcripts"
                  >
                    <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M0 0h48v48H0z" fill="none"></path> <g id="Shopicon"> <path d="M4,44l8-8h20c2.2,0,4-1.8,4-4v-4l8,8V8c0-2.2-1.8-4-4-4H16c-2.2,0-4,1.8-4,4v4H8c-2.2,0-4,1.8-4,4V44z M16,8h24v18.343 L37.657,24H16V8z M8,16h4v8c0,2.2,1.8,4,4,4h16v4H10.343L8,34.343V16z"></path> </g> </g></svg>
                    <span>Transcripts</span>
                    <div class="position-absolute top-0 start-100 translate-middle p-1 bg-danger rounded-circle">
                      <div class="visually-hidden">New alerts</div>
                    </div>
                  </Link>
                  <Link
                    className={`nav-link position-relative ${
                      location.pathname === "/whatsapp-chats" ? "active" : ""
                    }`}
                    to="/whatsapp-chats"
                    // title="Messages"
                    data-tooltip-id="TooltipMessages"
                    data-tooltip-content="Messages"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 9H17M7 13H12M21 20L17.6757 18.3378C17.4237 18.2118 17.2977 18.1488 17.1656 18.1044C17.0484 18.065 16.9277 18.0365 16.8052 18.0193C16.6672 18 16.5263 18 16.2446 18H6.2C5.07989 18 4.51984 18 4.09202 17.782C3.71569 17.5903 3.40973 17.2843 3.21799 16.908C3 16.4802 3 15.9201 3 14.8V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V20Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                    <span>Messages</span>
                    <div class="position-absolute top-0 start-100 translate-middle p-1 bg-danger rounded-circle">
                      <div class="visually-hidden">New alerts</div>
                    </div>
                  </Link>
                
                  <Link
                    className={`nav-link ${
                      location.pathname === "/settings" ? "active" : ""
                    }`}
                    to="/settings"
                    // title="Features"
                    data-tooltip-id="TooltipFeatures"
                    data-tooltip-content="Features"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4.9996 7V11M9.9996 2V6M17.9996 16V20M2.9996 9H6.9996M7.9996 4H11.9996M15.9996 18H19.9996M13.9996 7L16.828 9.82843M19.5162 3.74612L20.0819 4.3118C20.4779 4.70782 20.6759 4.90583 20.7501 5.13416C20.8154 5.335 20.8154 5.55135 20.7501 5.75219C20.6759 5.98052 20.4779 6.17853 20.0819 6.57454L6.52508 20.1314C6.12906 20.5274 5.93105 20.7254 5.70272 20.7996C5.50188 20.8649 5.28553 20.8649 5.08469 20.7996C4.85636 20.7254 4.65835 20.5274 4.26234 20.1314L3.69665 19.5657C3.30063 19.1697 3.10262 18.9717 3.02844 18.7433C2.96318 18.5425 2.96318 18.3262 3.02844 18.1253C3.10262 17.897 3.30063 17.699 3.69665 17.303L17.2535 3.74612C17.6495 3.3501 17.8475 3.15209 18.0758 3.0779C18.2767 3.01265 18.493 3.01265 18.6939 3.0779C18.9222 3.15209 19.1202 3.3501 19.5162 3.74612Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                    <span>Settings</span>
                  </Link>
                  {canManageUsers && (
                    <Link
                      className={`nav-link ${
                        location.pathname === "/manage-users" ? "active" : ""
                      }`}
                      to="/manage-users"
                      // title="User management"
                      data-tooltip-id="TooltipUserManagement"
                      data-tooltip-content="User Management"
                    >
                      <svg fill="#000000" viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg" stroke="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M168,100a60,60,0,1,1-60-60A60,60,0,0,1,168,100Z" opacity="0"></path> <path d="M144.14264,157.55817a68,68,0,1,0-72.28528,0A119.88963,119.88963,0,0,0,16.083,194.85156a8.00012,8.00012,0,0,0,12.25,10.293,104.007,104.007,0,0,1,159.33447,0,8.00012,8.00012,0,0,0,12.25-10.293A119.89043,119.89043,0,0,0,144.14264,157.55817ZM56,100a52,52,0,1,1,52,52A52.059,52.059,0,0,1,56,100Zm192.24854,43.07178-4.6568-2.68866a23.73294,23.73294,0,0,0,0-8.76624l4.6568-2.68866a8,8,0,1,0-8-13.85644l-4.66816,2.69519A23.99995,23.99995,0,0,0,228,113.3761V108a8,8,0,0,0-16,0v5.3761a23.99995,23.99995,0,0,0-7.58038,4.39087l-4.66816-2.69519a8,8,0,1,0-8,13.85644l4.6568,2.68866a23.73294,23.73294,0,0,0,0,8.76624l-4.6568,2.68866a8,8,0,1,0,8,13.85644l4.66816-2.69519A23.99995,23.99995,0,0,0,212,158.6239V164a8,8,0,0,0,16,0v-5.3761a23.99995,23.99995,0,0,0,7.58038-4.39087l4.66816,2.69519a8,8,0,0,0,8-13.85644ZM212,136a8,8,0,1,1,8,8A8.00917,8.00917,0,0,1,212,136Z"></path> </g></svg>
                      <span>User list</span>
                    </Link>
                  )}
                  <Link
                    className={`nav-link ${
                      location.pathname === "/manage-contacts" ? "active" : ""
                    }`}
                    to="/manage-contacts"
                    // title="Contact list"
                    data-tooltip-id="TooltipContactList"
                    data-tooltip-content="Contact list"
                  >
                    <svg fill="#000000" viewBox="0 0 16 16" id="contacts-16px" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path id="Path_131" data-name="Path 131" d="M33,6a2,2,0,1,0-2,2A2,2,0,0,0,33,6ZM31,7a1,1,0,1,1,1-1A1,1,0,0,1,31,7Zm7.5-1H38V3.5A3.5,3.5,0,0,0,34.5,0h-10a.5.5,0,0,0-.5.5V2.045h-.5a.5.5,0,0,0,0,1H24V6h-.5a.5.5,0,0,0,0,1H24v3h-.5a.5.5,0,0,0,0,1H24v3h-.5a.5.5,0,0,0,0,1H24v.5a.5.5,0,0,0,.5.5h10A3.5,3.5,0,0,0,38,12.5V10h.5a.5.5,0,0,0,.5-.5v-3A.5.5,0,0,0,38.5,6ZM38,9H37a1,1,0,0,1,0-2h1Zm-1,3.5A2.5,2.5,0,0,1,34.5,15h-9a.5.5,0,0,0,0-1H25V11h.5a.5.5,0,0,0,0-1H25V7h.5a.5.5,0,0,0,0-1H25V3.045h.545a.5.5,0,0,0,0-1H25V1h9.5A2.5,2.5,0,0,1,37,3.5V6a2,2,0,0,0,0,4ZM31.5,8h-1A3.5,3.5,0,0,0,27,11.5,1.5,1.5,0,0,0,28.5,13h5A1.5,1.5,0,0,0,35,11.5,3.5,3.5,0,0,0,31.5,8Zm2,4h-5a.5.5,0,0,1-.5-.5A2.5,2.5,0,0,1,30.5,9h1A2.5,2.5,0,0,1,34,11.5.5.5,0,0,1,33.5,12Z" transform="translate(-23)"></path> </g></svg>
                    <span>Contact list</span>
                  </Link>
                  {canAccessDeveloperTools && (
                    <Link
                      className={`nav-link ${
                        location.pathname === "/developer-access" ? "active" : ""
                      }`}
                      to="/developer-access"
                      data-tooltip-id="TooltipDeveloperAccess"
                      data-tooltip-content="Developer Access"
                    >
                      <svg fill="#000000" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M9.25 12a.75.75 0 0 1-.22.53l-2.75 2.75a.75.75 0 0 1-1.06-1.06L7.44 12 5.22 9.78a.75.75 0 1 1 1.06-1.06l2.75 2.75c.141.14.22.331.22.53zm2 2a.75.75 0 0 0 0 1.5h5.5a.75.75 0 0 0 0-1.5h-5.5z"></path><path fill-rule="evenodd" d="M0 4.75C0 3.784.784 3 1.75 3h20.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0 1 22.25 21H1.75A1.75 1.75 0 0 1 0 19.25V4.75zm1.75-.25a.25.25 0 0 0-.25.25v14.5c0 .138.112.25.25.25h20.5a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25H1.75z"></path></g></svg>
                      <span>Developer Access</span>
                    </Link>
                  )}
                  <Link
                    className={`nav-link ${
                      location.pathname === "/external-templates" ? "active" : ""
                    }`}
                    to="/external-templates"
                    data-tooltip-id="TooltipExternalTemplates"
                    data-tooltip-content="External Templates"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 12.2H15M8 16.2H12.38M10 3.67676V2.01221M14 3.67676V2.01221M7.2 7.70003H16.8C17.9201 7.70003 18.4802 7.70003 18.908 7.91639C19.2843 8.10807 19.5903 8.41421 19.782 8.79128C20 9.21692 20 9.77919 20 10.9037V16.8C20 17.9246 20 18.4868 19.782 18.9124C19.5903 19.2895 19.2843 19.5956 18.908 19.7873C18.4802 20.0037 17.9201 20.0037 16.8 20.0037H7.2C6.0799 20.0037 5.51984 20.0037 5.09202 19.7873C4.71569 19.5956 4.40973 19.2895 4.21799 18.9124C4 18.4868 4 17.9246 4 16.8V10.9037C4 9.77919 4 9.21692 4.21799 8.79128C4.40973 8.41421 4.71569 8.10807 5.09202 7.91639C5.51984 7.70003 6.07989 7.70003 7.2 7.70003Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                    <span>External Templates</span>
                  </Link>
                </ul>
              </div>
            </div>
          </div>
          <div className="menuSidebarBottom">
            <div
              className="userProfile dashboardBranding mb-3 mb-md-0"
              data-tooltip-id="brand-title"
              data-tooltip-content={organizationName}
              data-tooltip-place="right"
            >
              <div className="orgInitial" aria-hidden>
                {orgInitial}
              </div>
              <span>{organizationName}</span>
            </div>

            <div className="userProfile" onClick={(e) => setProfileDropdownShow(true)}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M18 18.7023C18 15.6706 14.5 15 12 15C9.5 15 6 15.6706 6 18.7023M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12ZM15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
              <span>My profile</span>
            </div>
            
            {
              profileDropdownShow ?
              <OutsideClickHandler onOutsideClick={(e) => setProfileDropdownShow(false)}>
                <div className="profileDropdown">
                  <div className="logOut profileDropdownItem" onClick={_logout}>
                    <i className="fa fa-power-off"></i> <span>Logout</span>
                  </div>
                </div>
              </OutsideClickHandler> : ""
            }
          </div>
          <Tooltip id="brand-title" />
        </div>
      </OutsideClickHandler>

      {/* Top Bar */}
      <div className="topbar">
        <div className="left">
          {/* <div className="userProfile" onClick={(e) => setProfileDropdownShow(true)}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M18 18.7023C18 15.6706 14.5 15 12 15C9.5 15 6 15.6706 6 18.7023M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12ZM15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </div> */}

          <div className="toggleSidebar" onClick={(e) => setToggleSidebar(true)}>
            <svg viewBox="-0.5 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M19 3.32001H16C14.8954 3.32001 14 4.21544 14 5.32001V8.32001C14 9.42458 14.8954 10.32 16 10.32H19C20.1046 10.32 21 9.42458 21 8.32001V5.32001C21 4.21544 20.1046 3.32001 19 3.32001Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M8 3.32001H5C3.89543 3.32001 3 4.21544 3 5.32001V8.32001C3 9.42458 3.89543 10.32 5 10.32H8C9.10457 10.32 10 9.42458 10 8.32001V5.32001C10 4.21544 9.10457 3.32001 8 3.32001Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M19 14.32H16C14.8954 14.32 14 15.2154 14 16.32V19.32C14 20.4246 14.8954 21.32 16 21.32H19C20.1046 21.32 21 20.4246 21 19.32V16.32C21 15.2154 20.1046 14.32 19 14.32Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M8 14.32H5C3.89543 14.32 3 15.2154 3 16.32V19.32C3 20.4246 3.89543 21.32 5 21.32H8C9.10457 21.32 10 20.4246 10 19.32V16.32C10 15.2154 9.10457 14.32 8 14.32Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </div>
        </div>

        
        <div className="right">
          <div className="orgName">
            <span>{organizationName}</span>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="page-wrapper">
        <div className="page-content-tab">
          <div className="container h-100">{children}</div>
        </div>
      </div>
      <QRViewer />


      {/* MOBILE BOTTOM BAR */}
      <div className="mobile-bottom-bar">
        <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/connect-contacts" ? "active" : ""
            }`}
            to="/connect-contacts"
            // title="Connect Contacts"
            data-tooltip-id="TooltipConnectContacts"
            data-tooltip-content="Connect Contacts"
          >
            <svg fill="#000000" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 465.00 465.00" stroke="#000000" stroke-width="10.695"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M117.5,173c34.462,0,62.5-28.037,62.5-62.5S151.962,48,117.5,48S55,76.037,55,110.5S83.038,173,117.5,173z M117.5,63 c26.191,0,47.5,21.309,47.5,47.5S143.691,158,117.5,158S70,136.691,70,110.5S91.309,63,117.5,63z"></path> <path d="M282.5,173c34.462,0,62.5-28.037,62.5-62.5S316.962,48,282.5,48S220,76.037,220,110.5S248.038,173,282.5,173z M282.5,63 c26.191,0,47.5,21.309,47.5,47.5S308.691,158,282.5,158S235,136.691,235,110.5S256.309,63,282.5,63z"></path> <path d="M363.656,256.882c1.478,1.912,3.697,2.915,5.94,2.914c1.601,0,3.215-0.511,4.581-1.565 c3.278-2.533,3.881-7.243,1.349-10.521C353.077,218.66,319.17,202,282.5,202c-23.545,0-46.264,6.94-65.7,20.071 c-3.433,2.319-4.335,6.981-2.017,10.413c2.32,3.434,6.982,4.336,10.413,2.017C242.144,223.052,261.959,217,282.5,217 C314.489,217,344.07,231.536,363.656,256.882z"></path> <path d="M117.5,202C52.71,202,0,254.71,0,319.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5C15,262.981,60.981,217,117.5,217 S220,262.981,220,319.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5C235,254.71,182.29,202,117.5,202z"></path> <path d="M392.5,272c-39.977,0-72.5,32.523-72.5,72.5s32.523,72.5,72.5,72.5s72.5-32.523,72.5-72.5S432.477,272,392.5,272z M392.5,402c-31.706,0-57.5-25.794-57.5-57.5s25.794-57.5,57.5-57.5s57.5,25.794,57.5,57.5S424.206,402,392.5,402z"></path> <path d="M417.5,337H400v-17.5c0-4.143-3.358-7.5-7.5-7.5s-7.5,3.357-7.5,7.5V337h-17.5c-4.142,0-7.5,3.357-7.5,7.5 s3.358,7.5,7.5,7.5H385v17.5c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5V352h17.5c4.142,0,7.5-3.357,7.5-7.5 S421.642,337,417.5,337z"></path> </g> </g></svg>
          </Link>
        </div>


        <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/transcript-chats" ? "active" : ""
            }`}
            to="/transcript-chats"
            // title="Messages"
            data-tooltip-id="TooltipTranscripts"
            data-tooltip-content="Transcripts"
          >
            <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M0 0h48v48H0z" fill="none"></path> <g id="Shopicon"> <path d="M4,44l8-8h20c2.2,0,4-1.8,4-4v-4l8,8V8c0-2.2-1.8-4-4-4H16c-2.2,0-4,1.8-4,4v4H8c-2.2,0-4,1.8-4,4V44z M16,8h24v18.343 L37.657,24H16V8z M8,16h4v8c0,2.2,1.8,4,4,4h16v4H10.343L8,34.343V16z"></path> </g> </g></svg>
          </Link>
        </div>
          


        <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/whatsapp-chats" ? "active" : ""
            }`}
            to="/whatsapp-chats"
            // title="Messages"
            data-tooltip-id="TooltipMessages"
            data-tooltip-content="Messages"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 9H17M7 13H12M21 20L17.6757 18.3378C17.4237 18.2118 17.2977 18.1488 17.1656 18.1044C17.0484 18.065 16.9277 18.0365 16.8052 18.0193C16.6672 18 16.5263 18 16.2446 18H6.2C5.07989 18 4.51984 18 4.09202 17.782C3.71569 17.5903 3.40973 17.2843 3.21799 16.908C3 16.4802 3 15.9201 3 14.8V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V20Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </Link>
        </div>


        <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/settings" ? "active" : ""
            }`}
            to="/settings"
            // title="Features"
            data-tooltip-id="TooltipFeatures"
            data-tooltip-content="Features"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4.9996 7V11M9.9996 2V6M17.9996 16V20M2.9996 9H6.9996M7.9996 4H11.9996M15.9996 18H19.9996M13.9996 7L16.828 9.82843M19.5162 3.74612L20.0819 4.3118C20.4779 4.70782 20.6759 4.90583 20.7501 5.13416C20.8154 5.335 20.8154 5.55135 20.7501 5.75219C20.6759 5.98052 20.4779 6.17853 20.0819 6.57454L6.52508 20.1314C6.12906 20.5274 5.93105 20.7254 5.70272 20.7996C5.50188 20.8649 5.28553 20.8649 5.08469 20.7996C4.85636 20.7254 4.65835 20.5274 4.26234 20.1314L3.69665 19.5657C3.30063 19.1697 3.10262 18.9717 3.02844 18.7433C2.96318 18.5425 2.96318 18.3262 3.02844 18.1253C3.10262 17.897 3.30063 17.699 3.69665 17.303L17.2535 3.74612C17.6495 3.3501 17.8475 3.15209 18.0758 3.0779C18.2767 3.01265 18.493 3.01265 18.6939 3.0779C18.9222 3.15209 19.1202 3.3501 19.5162 3.74612Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </Link>
        </div>


        <div className="item">
          <Link
            className={`itemLink ${
              location.pathname === "/external-templates" ? "active" : ""
            }`}
            to="/external-templates"
            data-tooltip-id="TooltipExternalTemplates"
            data-tooltip-content="External Templates"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 12.2H15M8 16.2H12.38M10 3.67676V2.01221M14 3.67676V2.01221M7.2 7.70003H16.8C17.9201 7.70003 18.4802 7.70003 18.908 7.91639C19.2843 8.10807 19.5903 8.41421 19.782 8.79128C20 9.21692 20 9.77919 20 10.9037V16.8C20 17.9246 20 18.4868 19.782 18.9124C19.5903 19.2895 19.2843 19.5956 18.908 19.7873C18.4802 20.0037 17.9201 20.0037 16.8 20.0037H7.2C6.0799 20.0037 5.51984 20.0037 5.09202 19.7873C4.71569 19.5956 4.40973 19.2895 4.21799 18.9124C4 18.4868 4 17.9246 4 16.8V10.9037C4 9.77919 4 9.21692 4.21799 8.79128C4.40973 8.41421 4.71569 8.10807 5.09202 7.91639C5.51984 7.70003 6.07989 7.70003 7.2 7.70003Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
